import type { CutsceneMode, Game } from '../game/Game';
import { bgrToRgbNumber, parseBgrString } from '../engine/KnyttUtil';
import { playUiClick, playUiSound } from './UiSound';

/**
 * A cutscene slideshow: shows `<name>/Scene1.png`, `Scene2.png`, … from the
 * world (bin or system data), navigated with ←/→ or click, over the cutscene's
 * background colour, with optional cutscene music. Ported from Cutscene.cs.
 */
export class Cutscene {
  private el: HTMLDivElement;
  private imgEl: HTMLImageElement;
  private nextBtn: HTMLButtonElement;
  private backBtn: HTMLButtonElement;
  private images: string[] = [];
  private current = 0;
  private music: HTMLAudioElement | null = null;
  private finished = false;
  private fadeEl: HTMLDivElement | null = null;

  private mode: CutsceneMode;
  private shiftSound: string | null;

  constructor(
    private parent: HTMLElement,
    private game: Game,
    private name: string,
    private onDone: () => void,
    opts: { sound: string | null; mode: CutsceneMode } = { sound: null, mode: 'middle' },
  ) {
    this.mode = opts.mode;
    this.shiftSound = opts.sound;
    const bgr = parseBgrString(
      game.world.iniData.getSection('Cutscene Color')?.get(name),
      0xffffff,
    );
    const bg = bgrToRgbNumber(bgr);

    this.el = document.createElement('div');
    this.el.className = 'cutscene';
    this.el.style.background = `#${bg.toString(16).padStart(6, '0')}`;
    this.el.innerHTML = `
      <img class="cutscene-img" alt="" />
      <button class="cutscene-nav cutscene-back" aria-label="Previous">‹</button>
      <button class="cutscene-nav cutscene-next" aria-label="Next">›</button>`;
    this.imgEl = this.el.querySelector('.cutscene-img') as HTMLImageElement;
    this.backBtn = this.el.querySelector('.cutscene-back') as HTMLButtonElement;
    this.nextBtn = this.el.querySelector('.cutscene-next') as HTMLButtonElement;
    this.backBtn.addEventListener('click', () => this.prev());
    this.nextBtn.addEventListener('click', () => this.next());
    window.addEventListener('keydown', this.onKey);
    parent.appendChild(this.el);

    void this.load();
  }

  private async load(): Promise<void> {
    for (let n = 1; n <= 99; n++) {
      const bytes = await this.game.assets.resolveBytes(`${this.name}/Scene${n}.png`);
      if (!bytes) break;
      this.images.push(URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'image/png' })));
    }
    if (this.images.length === 0) {
      this.finish(); // nothing to show
      return;
    }
    this.openFade();
    this.playMusic();
    this.show(0);
    // The invoking Shift's Sound plays inside the cutscene (Cutscene.cs:16-20 —
    // the game scene is detached, so it can't play in-world).
    if (this.shiftSound != null) void this.playWorldSound(this.shiftSound);
  }

  /**
   * Fade into the cutscene: intro fades the scene in from the cutscene colour over
   * 1s (GDKnyttDataStore.cs:92-93 CutsceneFadeIn); a mid-game Shift fades the game
   * to the colour fast (0.4s, Shift.cs:71-73 fade(fast:true)); the ending fades
   * slowly (1s, GDKnyttGame.cs:313) before the ending cutscene shows.
   */
  private openFade(): void {
    const overlay = document.createElement('div');
    overlay.className = 'cutscene-fade';
    overlay.style.background = this.el.style.background;
    const ms = this.mode === 'intro' || this.mode === 'ending' ? 1000 : 400;
    if (this.mode === 'intro') {
      // Cutscene already visible; the colour overlay fades out to reveal it.
      overlay.style.opacity = '1';
      overlay.style.transition = `opacity ${ms}ms linear`;
      this.el.appendChild(overlay);
      requestAnimationFrame(() => (overlay.style.opacity = '0'));
      setTimeout(() => overlay.remove(), ms + 50);
    } else {
      // Fade the (still-visible) game to the cutscene colour, then reveal the pages.
      this.el.style.background = 'transparent';
      this.imgEl.style.visibility = 'hidden';
      overlay.style.opacity = '0';
      overlay.style.transition = `opacity ${ms}ms linear`;
      this.el.appendChild(overlay);
      requestAnimationFrame(() => (overlay.style.opacity = '1'));
      setTimeout(() => {
        this.el.style.background = overlay.style.background;
        this.imgEl.style.visibility = '';
        overlay.remove();
      }, ms);
    }
  }

  /**
   * StandartSoundPlayer inside the cutscene: the game AudioContext is suspended, so
   * resolve the sound here — world Sounds/<name>.ogg first for door/electronic/
   * switch/teleport/unknown names, else the built-in Sfx cue.
   */
  private async playWorldSound(sound: string): Promise<void> {
    const builtins = ['door', 'electronic', 'switch', 'teleport', 'powerup', 'bounce', 'save'];
    const sfxNames = ['Door', 'Electronic', 'Switch', 'Teleport', 'Powerup', 'Bounce Platform', 'Save Spot'];
    const idx = builtins.indexOf(sound.toLowerCase());
    if (idx < 4 && this.game.world.worldFileExists(`Sounds/${sound}.ogg`)) {
      const bytes = await this.game.assets.resolveBytes(`Sounds/${sound}.ogg`);
      if (bytes) {
        const a = new Audio(URL.createObjectURL(new Blob([bytes as BlobPart])));
        a.muted = this.game.audio.muted;
        void a.play().catch(() => {});
        return;
      }
    }
    if (idx !== -1) playUiSound(`Sfx/${sfxNames[idx]}.flac`);
  }

  private async playMusic(): Promise<void> {
    const song = this.game.world.iniData.getSection('Cutscene Music')?.get(this.name);
    if (!song) return;
    let s = song.trim();
    const ambiance = s.endsWith('a');
    if (ambiance) s = s.slice(0, -1);
    const path = ambiance ? `Ambiance/Ambi${s}.ogg` : `Music/Song${s}.ogg`;
    const bytes = await this.game.assets.resolveBytes(path);
    if (!bytes) return;
    this.music = new Audio(URL.createObjectURL(new Blob([bytes as BlobPart])));
    this.music.volume = 0.6;
    // Honour the persisted mute (shared with AudioManager / menu) so testing stays silent.
    this.music.muted = this.game.audio.muted;
    this.music.play().catch(() => {});
  }

  private show(i: number): void {
    this.current = i;
    this.imgEl.src = this.images[i];
    this.backBtn.style.visibility = i > 0 ? 'visible' : 'hidden';
    this.nextBtn.textContent = i < this.images.length - 1 ? '›' : 'OK';
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.code === 'ArrowLeft') this.prev();
    else if (e.code === 'ArrowRight') this.next();
  };

  private prev(): void {
    if (this.current > 0) {
      playUiClick(); // Cutscene.cs:93 — Back is always the plain click
      this.show(this.current - 1);
    }
  }
  private next(): void {
    this.pageSound();
    if (this.current < this.images.length - 1) this.show(this.current + 1);
    else this.finish();
  }

  // Cutscene.cs:97-109: Next plays the world's <name>/sound{scene+1}.ogg if present
  // (scene is 1-based = this.current+1), else the menu click.
  private pageSound(): void {
    const path = `${this.name}/sound${this.current + 2}.ogg`;
    if (this.game.world.worldFileExists(path)) {
      void this.game.assets.resolveBytes(path).then((bytes) => {
        if (!bytes) return playUiClick();
        const a = new Audio(URL.createObjectURL(new Blob([bytes as BlobPart])));
        a.muted = this.game.audio.muted;
        void a.play().catch(() => {});
      });
    } else {
      playUiClick();
    }
  }

  /** Ports Cutscene.cs's endCutscene(): a synchronized visual + music fade-out, then cleanup. */
  private finish(): void {
    if (this.finished) return;
    this.finished = true;
    window.removeEventListener('keydown', this.onKey);

    const FADE_MS = 1000;
    this.fadeEl = document.createElement('div');
    this.fadeEl.className = 'cutscene-fade';
    this.fadeEl.style.background = this.el.style.background;
    this.fadeEl.style.opacity = '0';
    this.fadeEl.style.transition = `opacity ${FADE_MS}ms linear`;
    this.el.appendChild(this.fadeEl);

    const startVol = this.music?.volume ?? 0;
    const startTime = performance.now();
    const fadeMusic = () => {
      if (!this.music) return;
      const t = Math.min(1, (performance.now() - startTime) / FADE_MS);
      this.music.volume = startVol * (1 - t);
      if (t < 1) requestAnimationFrame(fadeMusic);
    };
    if (this.music) requestAnimationFrame(fadeMusic);

    requestAnimationFrame(() => {
      if (this.fadeEl) this.fadeEl.style.opacity = '1';
    });

    setTimeout(() => {
      if (this.music) this.music.pause();
      for (const url of this.images) URL.revokeObjectURL(url);
      this.el.remove();
      this.onDone();
    }, FADE_MS);
  }
}
